import type { StockMovementDTO } from "@/modules/inventory/domain/contracts";
import {
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";

type StockMovementRow = StockMovementDTO & {
  sku: string;
  productName: string | null;
  createdByName: string | null;
};

type StockMovementsTableProps = {
  isLoading: boolean;
  data: Array<StockMovementRow>;
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onPaginationChange: (offset: number, limit: number) => void;
  onSortingChange: (id: string, desc: boolean) => void;
};

function MovementTypeBadge({ type }: { type: string }) {
  const typeConfig: Record<string, { label: string; className: string }> = {
    adjustment: { label: "ປັບ", className: "bg-blue-100 text-blue-800" },
    sale: { label: "ຂາຍ", className: "bg-red-100 text-red-800" },
    return: { label: "ຄືນ", className: "bg-orange-100 text-orange-800" },
    restock: { label: "ເຕີມສະຕ໋ອກ", className: "bg-green-100 text-green-800" },
  };

  const config = typeConfig[type] || {
    label: type,
    className: "bg-gray-100 text-gray-800",
  };

  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 font-medium text-xs ${config.className}`}
    >
      {config.label}
    </span>
  );
}

function DeltaDisplay({ delta }: { delta: number }) {
  const isPositive = delta > 0;
  return (
    <span
      className={`font-medium ${
        isPositive ? "text-green-600" : "text-red-600"
      }`}
    >
      {isPositive ? "+" : ""}
      {delta}
    </span>
  );
}

export function StockMovementsTable({
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onPaginationChange,
  onSortingChange,
}: StockMovementsTableProps) {
  const columns: TanstackReactTable.ColumnDef<StockMovementRow>[] = [
    createSortableColumn<StockMovementRow>("createdAt", "ວັນທີ", {
      size: 150,
      cell: ({ row }) =>
        new Date(row.original.createdAt).toLocaleString("lo-LA"),
    }),
    createSortableColumn<StockMovementRow>("productName", "ສິນຄ້າ", {
      size: 200,
      cell: ({ row }) => row.original.productName || "—",
    }),
    createSortableColumn<StockMovementRow>("sku", "SKU", {
      size: 120,
      cell: ({ row }) => row.original.sku,
    }),
    createSortableColumn<StockMovementRow>("type", "ປະເພດ", {
      size: 100,
      cell: ({ row }) => <MovementTypeBadge type={row.original.type} />,
    }),
    createSortableColumn<StockMovementRow>("quantityDelta", "ຈຳນວນ", {
      size: 80,
      cell: ({ row }) => <DeltaDisplay delta={row.original.quantityDelta} />,
    }),
    createSortableColumn<StockMovementRow>("refId", "ອ້າງອີງ", {
      size: 100,
      cell: ({ row }) => row.original.refId || "—",
    }),
    createSortableColumn<StockMovementRow>("note", "ໝາຍເຫດ", {
      size: 150,
      cell: ({ row }) => row.original.note || "—",
    }),
    createSortableColumn<StockMovementRow>("createdByName", "ຜູ້ທຳ", {
      size: 100,
      cell: ({ row }) =>
        row.original.createdByName ?? row.original.createdBy ?? "—",
    }),
  ];

  return (
    <DataTable<StockMovementRow, unknown>
      noDataMessage="ບໍ່ພົບບັນທຶກການເຄື່ອນໄຫວ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(s) => {
        if (s[0]?.id === "") return;
        onSortingChange(s[0]?.id as string, !!s[0]?.desc);
      }}
    />
  );
}
